import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Megaphone, Pencil, ArrowUpRight, Loader2, Upload, X, Plus, Trash2, Star } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ALIGN = { left: "justify-start text-left", center: "justify-center text-center", right: "justify-end text-right" };

export default function TopBanner() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [banner, setBanner] = useState(null);
  const [editing, setEditing] = useState(false);
  const [idx, setIdx] = useState(0);

  const load = () => api.get("/banner").then((r) => setBanner(r.data)).catch(() => setBanner(null));
  useEffect(() => { load(); }, []);

  const ads = banner?.ads || [];
  const show = !!banner?.enabled && ads.length > 0;

  useEffect(() => { setIdx(0); }, [banner]);
  useEffect(() => {
    if (!show || ads.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % ads.length), (banner.rotate_seconds || 6) * 1000);
    return () => clearInterval(t);
  }, [show, ads.length, banner]);

  if (!banner) return null;
  if (!show && !isAdmin) return null;

  const ad = ads[idx] || ads[0];

  const trackClick = (id) => {
    if (!id) return;
    try {
      const url = `${BACKEND}/api/banner/click/${id}`;
      if (navigator.sendBeacon) navigator.sendBeacon(url);
      else api.post(`/banner/click/${id}`).catch((err) => console.error("Failed to record banner click:", err));
    } catch (error) {
      console.error("Failed to track banner click:", error);
    }
  };

  const Dots = ({ className, dark }) => (
    ads.length > 1 ? (
      <span className={className} data-testid="banner-dots">
        {ads.map((d, i) => (
          <button key={d.id || `dot-${i}`} type="button" aria-label={`Ad ${i + 1}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx(i); }} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? (dark ? "bg-slate-800" : "bg-white") : (dark ? "bg-slate-300" : "bg-white/40")}`} />
        ))}
      </span>
    ) : null
  );

  return (
    <>
      <div
        data-testid="top-banner"
        className={`relative w-full ${show ? "bg-gradient-to-r from-[#0B1934] via-[#12306b] to-[#0B1934] text-white" : "bg-secondary/60 text-muted-foreground border-b border-dashed border-border"}`}
      >
        {show && ad?.image_url ? (
          <div className="w-full flex justify-center px-2 sm:px-4 py-2">
            <a
              href={ad.link_url || undefined}
              onClick={() => trackClick(ad.id)}
              target="_blank"
              rel="sponsored noopener noreferrer"
              data-testid="top-banner-link"
              className="relative w-fit min-w-[4.5in] max-w-full flex items-center gap-3 sm:gap-4 rounded-md border border-slate-200 bg-white px-3 sm:px-5 py-2.5 shadow-sm hover:shadow-md transition-shadow"
            >
              <img src={ad.image_url} alt={ad.text || "Advertisement"} data-testid="top-banner-image" className="h-12 sm:h-16 w-auto max-w-[40%] shrink-0 rounded object-contain bg-white" />
              <div className="min-w-0 max-w-md pr-6">
                <div className="flex items-baseline gap-x-2 gap-y-0.5 flex-wrap leading-tight">
                  {ad.brand ? <span className="text-slate-600 text-sm sm:text-base">{ad.brand}</span> : null}
                  <span className="font-bold text-slate-900 text-sm sm:text-base line-clamp-2">{ad.text}</span>
                </div>
                {(ad.rating || ad.reviews) ? (
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-600" data-testid="top-banner-rating">
                    {ad.rating ? <span className="font-semibold text-slate-800">{Number(ad.rating).toFixed(1)}</span> : null}
                    {ad.rating ? (
                      <span className="flex items-center">
                        {[0, 1, 2, 3, 4].map((s) => (
                          <Star key={s} className={`w-3.5 h-3.5 ${s < Math.round(Number(ad.rating)) ? "fill-amber-400 text-amber-400" : "fill-slate-200 text-slate-200"}`} />
                        ))}
                      </span>
                    ) : null}
                    {ad.reviews ? <span className="text-slate-500">{Number.isFinite(Number(ad.reviews)) ? Number(ad.reviews).toLocaleString() : ad.reviews}</span> : null}
                  </div>
                ) : null}
              </div>
              {ad.cta ? <span className="hidden sm:block shrink-0 text-sm font-semibold text-slate-800 pr-4" data-testid="top-banner-cta">{ad.cta}</span> : null}
              <span className="absolute top-1 right-2 inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                Ad<span className="w-0 h-0 border-l-[3px] border-r-[3px] border-t-[5px] border-l-transparent border-r-transparent border-t-slate-300" />
              </span>
              <Dots dark className="absolute bottom-1 right-2 flex items-center gap-1" />
            </a>
          </div>
        ) : (
          <div className="max-w-[1500px] mx-auto px-4 sm:px-10 py-2 text-sm">
            <div className={`flex items-center gap-3 min-h-[1.75rem] ${show ? ALIGN[banner.position] || ALIGN.center : "justify-center"}`}>
              {show ? (
                <>
                  <span className="inline-flex items-center rounded bg-white/15 text-white/80 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide shrink-0" title="Sponsored / affiliate link">Ad</span>
                  {ad.link_url ? (
                    <a href={ad.link_url} onClick={() => trackClick(ad.id)} target="_blank" rel="sponsored noopener noreferrer" data-testid="top-banner-link" className="inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity min-w-0">
                      <span className="inline-flex items-center gap-2 min-w-0"><Megaphone className="w-4 h-4 shrink-0 opacity-80" /><span className="truncate font-semibold">{ad.text}</span></span>
                      <ArrowUpRight className="w-4 h-4 shrink-0" />
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-2 min-w-0"><Megaphone className="w-4 h-4 shrink-0 opacity-80" /><span className="truncate font-semibold">{ad.text}</span></span>
                  )}
                  <Dots className="hidden sm:flex items-center gap-1 shrink-0 ml-1" />
                </>
              ) : (
                <span className="text-xs italic">No ads yet — click the pencil to add Amazon / Expedia ads and enable the banner.</span>
              )}
            </div>
          </div>
        )}
        {isAdmin && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid="top-banner-edit-btn"
            aria-label="Edit banner ads"
            className={`absolute top-1.5 right-3 w-7 h-7 grid place-items-center rounded-full transition-colors z-10 ${show ? "bg-white/15 text-white hover:bg-white/25" : "bg-background text-muted-foreground hover:text-foreground border border-border"}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {isAdmin && <BannerEditor open={editing} onOpenChange={setEditing} banner={banner} onSaved={() => { setEditing(false); load(); }} />}
    </>
  );
}

function BannerEditor({ open, onOpenChange, banner, onSaved }) {
  const [enabled, setEnabled] = useState(false);
  const [position, setPosition] = useState("center");
  const [rotate, setRotate] = useState(6);
  const [ads, setAds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(-1);
  const [stats, setStats] = useState({});
  const fileRefs = useRef({});

  useEffect(() => {
    if (!open) return;
    setEnabled(!!banner?.enabled);
    setPosition(banner?.position || "center");
    setRotate(banner?.rotate_seconds || 6);
    setAds((banner?.ads || []).map((a) => ({ ...a })));
    api.get("/banner/stats").then((r) => {
      const m = {};
      (r.data.stats || []).forEach((s) => { m[s.id] = s.clicks; });
      setStats(m);
    }).catch(() => setStats({}));
  }, [open, banner]);

  const update = (i, patch) => setAds((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  const addAd = () => setAds((prev) => [...prev, { id: "", text: "", brand: "", rating: "", reviews: "", cta: "", image_url: "", link_url: "" }]);
  const removeAd = (i) => setAds((prev) => prev.filter((_, idx) => idx !== i));

  const upload = async (i, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB");
    setUploadingIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/articles/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update(i, { image_url: `${BACKEND}${data.url}` });
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploadingIdx(-1); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payloadAds = ads.map((a) => ({
        ...a,
        rating: a.rating === "" || a.rating == null ? null : Number(a.rating),
        reviews: a.reviews === "" || a.reviews == null ? null : parseInt(a.reviews, 10),
      }));
      await api.put("/banner", { enabled, position, rotate_seconds: Number(rotate) || 6, ads: payloadAds });
      toast.success("Banner ads saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="banner-editor" className="max-h-[92vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Banner ads</DialogTitle>
          <DialogDescription>Add your Amazon / Expedia affiliate ads. They rotate automatically and open in a new tab as sponsored links.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <button type="button" onClick={() => setEnabled((v) => !v)} data-testid="banner-enabled-toggle" className="w-full flex items-center justify-between rounded-lg border border-border px-4 py-3 text-sm">
            <span className="font-medium">Show banner</span>
            <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? "bg-emerald-600" : "bg-muted"}`}>
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-5" : "translate-x-0.5"}`} />
            </span>
          </button>

          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">Alignment</label>
              <div className="mt-1 inline-flex rounded-lg bg-secondary p-1 w-full">
                {["left", "center", "right"].map((p) => (
                  <button key={p} type="button" data-testid={`banner-position-${p}`} onClick={() => setPosition(p)} className={`flex-1 capitalize px-2 py-1.5 rounded-md text-xs font-semibold transition-colors ${position === p ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}>{p}</button>
                ))}
              </div>
            </div>
            <div className="w-28">
              <label className="text-xs text-muted-foreground">Rotate (sec)</label>
              <Input type="number" min={2} max={60} value={rotate} onChange={(e) => setRotate(e.target.value)} data-testid="banner-rotate-input" className="mt-1" />
            </div>
          </div>

          <div className="space-y-3" data-testid="banner-ads-list">
            {ads.map((a, i) => (
              <div key={a.id || `ad-row-${i}`} className="rounded-xl border border-border p-3 space-y-2 relative" data-testid={`banner-ad-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground">Ad {i + 1}{a.id && stats[a.id] != null ? ` · ${stats[a.id]} click${stats[a.id] === 1 ? "" : "s"}` : ""}</span>
                  <button type="button" onClick={() => removeAd(i)} data-testid={`banner-ad-remove-${i}`} className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-xs"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
                </div>
                <Input value={a.link_url || ""} onChange={(e) => update(i, { link_url: e.target.value })} data-testid={`banner-ad-link-${i}`} placeholder="Affiliate link (https://link.amazon/… or https://expedia.com/…)" />
                <Input value={a.brand || ""} onChange={(e) => update(i, { brand: e.target.value })} maxLength={80} data-testid={`banner-ad-brand-${i}`} placeholder="Brand (e.g. Vital Proteins)" />
                <Input value={a.text || ""} onChange={(e) => update(i, { text: e.target.value })} maxLength={200} data-testid={`banner-ad-text-${i}`} placeholder="Product title / description" />
                <div className="grid grid-cols-3 gap-2">
                  <Input type="number" step="0.1" min={0} max={5} value={a.rating ?? ""} onChange={(e) => update(i, { rating: e.target.value })} data-testid={`banner-ad-rating-${i}`} placeholder="Rating 0-5" />
                  <Input type="number" min={0} value={a.reviews ?? ""} onChange={(e) => update(i, { reviews: e.target.value })} data-testid={`banner-ad-reviews-${i}`} placeholder="# reviews" />
                  <Input value={a.cta || ""} onChange={(e) => update(i, { cta: e.target.value })} maxLength={60} data-testid={`banner-ad-cta-${i}`} placeholder="CTA" />
                </div>
                <input ref={(el) => (fileRefs.current[i] = el)} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={(e) => { upload(i, e.target.files?.[0]); e.target.value = ""; }} />
                {a.image_url ? (
                  <div className="relative rounded-lg border border-border bg-[#0B1934] p-2 flex items-center justify-center">
                    <img src={a.image_url} alt="" className="max-h-14 w-auto object-contain" data-testid={`banner-ad-image-${i}`} />
                    <button type="button" onClick={() => update(i, { image_url: "" })} data-testid={`banner-ad-image-remove-${i}`} aria-label="Remove image" className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black/80"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => fileRefs.current[i]?.click()} disabled={uploadingIdx === i} data-testid={`banner-ad-upload-${i}`}>
                    {uploadingIdx === i ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />} Upload image (optional)
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="secondary" className="w-full gap-1.5" onClick={addAd} data-testid="banner-add-ad-btn"><Plus className="w-4 h-4" /> Add ad</Button>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="banner-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="banner-save-btn">{saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
